import { useEffect, useState, ChangeEvent } from 'react';
import { rosterApi } from '../../api/client';

interface Student {
  id: number;
  student_id: string;
  last_name: string;
  first_name: string;
  gender: string;
  has_answered: boolean;
  has_pair: boolean;
  is_assigned: boolean;
}

export default function Roster() {
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [msg, setMsg] = useState('');
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<Student>>({});
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ student_id: '', last_name: '', first_name: '', gender: 'male' });

  const load = async () => {
    setLoading(true);
    try {
      const res = await rosterApi.list();
      setStudents(res.data);
    } catch (e: any) {
      setMsg('加载名单失败:' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const handleUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setMsg('正在上传并导入,请稍候...');
    setUploadResult(null);
    try {
      const res = await rosterApi.upload(file);
      setUploadResult(res.data);
      setMsg('');
      await load();
    } catch (err: any) {
      setMsg('上传失败:' + (err.response?.data?.detail || err.message));
    } finally {
      e.target.value = '';
    }
  };

  const handleExport = async () => {
    try {
      const res = await rosterApi.exportFile();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = res.headers['content-disposition'] || '';
      const m = cd.match(/filename\*=UTF-8''(.+)/);
      a.download = m ? decodeURIComponent(m[1]) : '名单.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert('导出失败:' + (err.response?.data?.detail || err.message));
    }
  };

  const startEdit = (s: Student) => { setEditingId(s.id); setEditForm({ ...s }); };
  const cancelEdit = () => { setEditingId(null); setEditForm({}); };
  const saveEdit = async (id: number) => {
    try {
      await rosterApi.update(id, {
        student_id: editForm.student_id,
        last_name: editForm.last_name,
        first_name: editForm.first_name,
        gender: editForm.gender,
      });
      cancelEdit();
      await load();
    } catch (err: any) {
      alert('保存失败:' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (s: Student) => {
    if (!window.confirm('确认删除 ' + s.student_id + '(' + s.last_name + ' ' + s.first_name + ')?')) return;
    try {
      await rosterApi.remove(s.id, false);
      await load();
    } catch (err: any) {
      if (err.response?.status === 409) {
        const ok = window.confirm((err.response.data.detail || '该学生已有数据') + '\n\n仍要删除并清除其全部数据吗?');
        if (ok) {
          try { await rosterApi.remove(s.id, true); await load(); }
          catch (e2: any) { alert('删除失败:' + (e2.response?.data?.detail || e2.message)); }
        }
      } else {
        alert('删除失败:' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleAdd = async () => {
    if (!addForm.student_id.trim()) { alert('请填学号'); return; }
    try {
      await rosterApi.add(addForm);
      setShowAdd(false);
      setAddForm({ student_id: '', last_name: '', first_name: '', gender: 'male' });
      await load();
    } catch (err: any) {
      alert('添加失败:' + (err.response?.data?.detail || err.message));
    }
  };

  const gLabel = (g: string) => (g === 'male' ? '男' : g === 'female' ? '女' : g);
  const filtered = students.filter((s) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      s.student_id.toLowerCase().includes(q) ||
      (s.last_name || '').toLowerCase().includes(q) ||
      (s.first_name || '').toLowerCase().includes(q)
    );
  });
  const male = students.filter((s) => s.gender === 'male').length;
  const female = students.filter((s) => s.gender === 'female').length;
  const answered = students.filter((s) => s.has_answered).length;

  return (
    <div className="max-w-5xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-2">名单管理</h1>
      <p className="text-gray-600 mb-4">
        共 {students.length} 人(男 {male} / 女 {female}),已答题 {answered} 人
      </p>

      <div className="border rounded-lg p-4 mb-4 bg-gray-50">
        <div className="font-medium mb-2">上传 Excel 名单(.xlsx)</div>
        <p className="text-sm text-gray-500 mb-2">
          表头需含:SID / Last Name / First Name / Gender。上传只会<strong>新增或更新</strong>,不会删除任何学生。
        </p>
        <input type="file" accept=".xlsx,.xlsm" onChange={handleUpload} />
        {uploadResult && (
          <div className="mt-2 text-sm text-green-700">
            导入完成:新增 {uploadResult.added}、更新 {uploadResult.updated}、跳过 {uploadResult.skipped}。
            {uploadResult.in_db_not_in_file > 0 && (
              <span className="text-gray-500">(库中另有 {uploadResult.in_db_not_in_file} 人不在本次文件里,未删除)</span>
            )}
            {uploadResult.errors?.length > 0 && (
              <ul className="text-red-600 mt-1 list-disc pl-5">
                {uploadResult.errors.map((er: string, i: number) => <li key={i}>{er}</li>)}
              </ul>
            )}
          </div>
        )}
      </div>

      {msg && <div className="mb-3 text-blue-700">{msg}</div>}

      <div className="flex items-center gap-2 mb-3">
        <input
          className="border rounded px-3 py-2 flex-1"
          placeholder="搜索学号 / 姓名"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className="bg-gray-600 text-white px-4 py-2 rounded" onClick={handleExport}>
          导出 Excel
        </button>
        <button className="bg-blue-600 text-white px-4 py-2 rounded" onClick={() => setShowAdd(!showAdd)}>
          {showAdd ? '取消' : '+ 添加学生'}
        </button>
      </div>

      {showAdd && (
        <div className="border rounded-lg p-3 mb-3 flex flex-wrap gap-2 items-center bg-blue-50">
          <input className="border rounded px-2 py-1" placeholder="学号" value={addForm.student_id}
            onChange={(e) => setAddForm({ ...addForm, student_id: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="姓 Last Name" value={addForm.last_name}
            onChange={(e) => setAddForm({ ...addForm, last_name: e.target.value })} />
          <input className="border rounded px-2 py-1" placeholder="名 First Name" value={addForm.first_name}
            onChange={(e) => setAddForm({ ...addForm, first_name: e.target.value })} />
          <select className="border rounded px-2 py-1" value={addForm.gender}
            onChange={(e) => setAddForm({ ...addForm, gender: e.target.value })}>
            <option value="male">男</option>
            <option value="female">女</option>
          </select>
          <button className="bg-green-600 text-white px-3 py-1 rounded" onClick={handleAdd}>保存</button>
        </div>
      )}

      {loading ? (
        <div>加载中...</div>
      ) : (
        <table className="w-full text-sm border">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">学号</th>
              <th className="p-2 text-left">姓</th>
              <th className="p-2 text-left">名</th>
              <th className="p-2 text-left">性别</th>
              <th className="p-2 text-left">状态</th>
              <th className="p-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s) => (
              <tr key={s.id} className="border-t">
                {editingId === s.id ? (
                  <>
                    <td className="p-2"><input className="border rounded px-1 w-28" value={editForm.student_id || ''}
                      onChange={(e) => setEditForm({ ...editForm, student_id: e.target.value })} /></td>
                    <td className="p-2"><input className="border rounded px-1 w-24" value={editForm.last_name || ''}
                      onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} /></td>
                    <td className="p-2"><input className="border rounded px-1 w-24" value={editForm.first_name || ''}
                      onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} /></td>
                    <td className="p-2">
                      <select className="border rounded px-1" value={editForm.gender || ''}
                        onChange={(e) => setEditForm({ ...editForm, gender: e.target.value })}>
                        <option value="male">男</option>
                        <option value="female">女</option>
                      </select>
                    </td>
                    <td className="p-2 text-gray-400">—</td>
                    <td className="p-2">
                      <button className="text-green-600 mr-2" onClick={() => saveEdit(s.id)}>保存</button>
                      <button className="text-gray-500" onClick={cancelEdit}>取消</button>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-2 font-mono">{s.student_id}</td>
                    <td className="p-2">{s.last_name}</td>
                    <td className="p-2">{s.first_name}</td>
                    <td className="p-2">{gLabel(s.gender)}</td>
                    <td className="p-2 space-x-1">
                      {s.has_answered && <span className="text-xs bg-green-100 text-green-700 px-1 rounded">已答题</span>}
                      {s.has_pair && <span className="text-xs bg-purple-100 text-purple-700 px-1 rounded">已配对</span>}
                      {s.is_assigned && <span className="text-xs bg-orange-100 text-orange-700 px-1 rounded">已分配</span>}
                    </td>
                    <td className="p-2">
                      <button className="text-blue-600 mr-2" onClick={() => startEdit(s)}>编辑</button>
                      <button className="text-red-600" onClick={() => handleDelete(s)}>删除</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-gray-400 mt-2">显示 {filtered.length} / {students.length} 人</p>
    </div>
  );
}
